import { motion, AnimatePresence } from 'framer-motion';
import {
  Sun,
  Moon,
  Bell,
  Search,
  ChevronRight,
  Settings,
  LogOut,
  SlidersHorizontal,
  PanelLeftClose,
  PanelLeft,
  User,
  CalendarClock,
  CalendarDays,
  Users,
  Inbox,
  Wallet,
  Package,
  Landmark,
  LayoutList,
  FileStack,
  Sparkles,
} from 'lucide-react';
import { useEffect, useMemo, useState, useRef } from 'react';
import type { ComponentType, SVGProps } from 'react';
import { NavLink, Outlet, useLocation, useNavigate, Link } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import {
  ClientsIcon,
  DashboardIcon,
  MenuIcon,
  QuestionnairesIcon,
  ReportsIcon,
  TemplatesIcon,
} from '../components/icons';
import { useSidebarCollapsed } from '../hooks/useSidebarCollapsed';
import { useTheme } from '../hooks/useTheme';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '../lib/hooks/useNotifications';

interface NavChild {
  to: string;
  label: string;
  /** RBAC permission key required to see this child (see `PERMISSIONS` in
   * `@brainkey/shared`). Omitted = visible to any authenticated coach. */
  permission?: string;
}

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  end?: boolean;
  /** RBAC permission key required to see this item. Omitted = visible to
   * any authenticated coach. */
  permission?: string;
  /** Optional submenu (e.g. Templates > Report templates / Questionnaire
   * templates). Only rendered in expanded (non-rail) sidebar mode. */
  children?: NavChild[];
}

/** A child's `to` may carry a query string (e.g. `/templates?tab=report`) —
 * `NavLink`'s own `isActive` only matches pathname, so active-state for
 * children is computed manually against the current location. */
function isChildActive(pathname: string, search: string, childTo: string): boolean {
  const [childPath, childQuery] = childTo.split('?');
  if (pathname !== childPath) return false;
  if (!childQuery) return search === '';
  const params = new URLSearchParams(search);
  const childParams = new URLSearchParams(childQuery);
  for (const [key, value] of childParams) {
    if (params.get(key) !== value) return false;
  }
  return true;
}

interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    id: 'core',
    label: 'Core',
    items: [
      { to: '/', label: 'Dashboard', icon: DashboardIcon, end: true },
      { to: '/clients', label: 'Clients', icon: ClientsIcon, permission: 'clients:view' },
      { to: '/leads', label: 'Leads', icon: Inbox, permission: 'leads:view' },
      { to: '/payments', label: 'Payments', icon: Wallet, permission: 'payments:view' },
    ],
  },
  {
    id: 'assessments',
    label: 'Assessments',
    items: [
      {
        to: '/questionnaires',
        label: 'Questionnaires',
        icon: QuestionnairesIcon,
        permission: 'questionnaires:view',
      },
      { to: '/reports', label: 'Reports', icon: ReportsIcon, permission: 'reports:view' },
      {
        to: '/templates',
        label: 'Templates',
        icon: TemplatesIcon,
        children: [
          { to: '/templates?tab=html', label: 'Report templates' },
          { to: '/templates?tab=questionnaire', label: 'Questionnaire templates' },
        ],
      },
    ],
  },
  {
    id: 'resources',
    label: 'Resources',
    items: [
      {
        to: '/resources',
        label: 'Resources',
        icon: FileStack,
        permission: 'resources:view',
      },
    ],
  },
  {
    id: 'scheduling',
    label: 'Scheduling',
    items: [
      { to: '/availability', label: 'Availability', icon: CalendarDays },
      {
        to: '/bookings',
        label: 'Bookings',
        icon: CalendarClock,
        children: [
          { to: '/bookings/today', label: 'Today' },
          { to: '/bookings', label: 'All bookings' },
        ],
      },
    ],
  },
  {
    id: 'team',
    label: 'Team',
    items: [
      {
        to: '/team',
        label: 'Team',
        icon: Users,
        permission: 'team:view',
        children: [
          { to: '/team/roles', label: 'Roles & Permissions', permission: 'roles:manage' },
          { to: '/audit-log', label: 'Audit Log', permission: 'audit:view' },
        ],
      },
    ],
  },
  {
    id: 'workspace-settings',
    label: 'Settings',
    items: [
      {
        to: '/settings/packages',
        label: 'Packages',
        icon: Package,
        permission: 'settings:workspace-manage',
      },
      {
        to: '/settings/bank-accounts',
        label: 'Bank Accounts',
        icon: Landmark,
        permission: 'settings:workspace-manage',
      },
      {
        to: '/settings/report-sections',
        label: 'Report Sections',
        icon: LayoutList,
        permission: 'settings:workspace-manage',
      },
    ],
  },
  {
    id: 'help',
    label: 'Help',
    items: [{ to: '/whats-new', label: 'What’s new', icon: Sparkles }],
  },
];

function titleForPath(pathname: string): string {
  if (pathname === '/') return 'Dashboard';
  if (pathname.startsWith('/clients')) return 'Clients';
  if (pathname.startsWith('/leads')) return 'Leads';
  if (pathname.startsWith('/payments')) return 'Payments';
  if (pathname.startsWith('/questionnaires')) return 'Questionnaires';
  if (pathname.startsWith('/reports')) return 'Reports';
  if (pathname.startsWith('/resources')) return 'Resources';
  if (pathname.startsWith('/availability')) return 'Availability';
  if (pathname.startsWith('/bookings/today')) return "Today's Bookings";
  if (pathname.startsWith('/bookings')) return 'Bookings';
  if (pathname.startsWith('/team/roles')) return 'Roles & Permissions';
  if (pathname.startsWith('/team')) return 'Team';
  if (pathname.startsWith('/audit-log')) return 'Audit Log';
  if (pathname.startsWith('/settings/packages')) return 'Packages';
  if (pathname.startsWith('/settings/bank-accounts')) return 'Bank Accounts';
  if (pathname.startsWith('/settings/report-sections')) return 'Report Sections';
  if (pathname.startsWith('/whats-new')) return 'What’s new';
  return 'Dashboard';
}

interface BreadcrumbSegment {
  label: string;
  to?: string;
}

/** Detail/create pages nested under a list route get a clickable parent
 * segment (e.g. Clients / Client Details) so the page itself doesn't need
 * its own "Back to X"-style link — the breadcrumb is the one way back. */
function breadcrumbForPath(pathname: string): BreadcrumbSegment[] {
  const clientMatch = /^\/clients\/([^/]+)/.exec(pathname);
  if (clientMatch) {
    const clientTo = `/clients/${clientMatch[1]}`;
    if (pathname.endsWith('/assessments/new')) {
      return [
        { label: 'Clients', to: '/clients' },
        { label: 'Client Details', to: clientTo },
        { label: 'New Assessment' },
      ];
    }
    if (pathname.endsWith('/questionnaires/new')) {
      return [
        { label: 'Clients', to: '/clients' },
        { label: 'Client Details', to: clientTo },
        { label: 'New Questionnaire' },
      ];
    }
    return [{ label: 'Clients', to: '/clients' }, { label: 'Client Details' }];
  }

  if (pathname === '/questionnaires/new') {
    return [{ label: 'Questionnaires', to: '/questionnaires' }, { label: 'New Questionnaire' }];
  }

  if (pathname.startsWith('/templates/html/')) {
    const label = pathname.endsWith('/edit') ? 'Edit Report Template' : 'New Report Template';
    return [{ label: 'Templates', to: '/templates?tab=html' }, { label }];
  }

  if (pathname.startsWith('/templates/questionnaire/')) {
    const label = pathname.endsWith('/edit')
      ? 'Edit Questionnaire Template'
      : 'New Questionnaire Template';
    return [{ label: 'Templates', to: '/templates?tab=questionnaire' }, { label }];
  }

  if (/^\/reports\/[^/]+$/.exec(pathname)) {
    return [{ label: 'Reports', to: '/reports' }, { label: 'Report Details' }];
  }

  return [{ label: titleForPath(pathname) }];
}

const SIDEBAR_RAIL_WIDTH = 72;
const SIDEBAR_EXPANDED_WIDTH = 260;

export function AppShell(): JSX.Element {
  const { coach, logout, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { collapsed, toggleCollapsed } = useSidebarCollapsed();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  const notifications = useNotifications();
  const markNotificationRead = useMarkNotificationRead();
  const markAllNotificationsRead = useMarkAllNotificationsRead();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        userMenuOpen &&
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
      if (
        notificationsOpen &&
        notificationsRef.current &&
        !notificationsRef.current.contains(event.target as Node)
      ) {
        setNotificationsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen, notificationsOpen]);

  const toggleGroup = (id: string) => {
    setCollapsedGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Defaults open whenever the current route belongs to this item, so
  // navigating to /templates auto-reveals its submenu; the coach can still
  // collapse it manually afterward.
  const isItemExpanded = (to: string): boolean =>
    expandedItems[to] ?? location.pathname.startsWith(to);
  const toggleItem = (to: string) => {
    setExpandedItems((prev) => ({ ...prev, [to]: !isItemExpanded(to) }));
  };

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Hide anything the coach's role isn't permitted to use — a group
  // disappears entirely once every one of its items is filtered out.
  const visibleNavGroups = useMemo(
    () =>
      NAV_GROUPS.map((group) => ({
        ...group,
        items: group.items
          .filter((item) => item.permission === undefined || hasPermission(item.permission))
          .map((item) => ({
            ...item,
            children: item.children?.filter(
              (child) => child.permission === undefined || hasPermission(child.permission),
            ),
          })),
      })).filter((group) => group.items.length > 0),
    [hasPermission],
  );

  const SidebarContent = ({ rail = false }: { rail?: boolean }) => (
    <>
      <div className={`flex shrink-0 items-center h-16 ${rail ? 'justify-center px-2' : 'px-4'}`}>
        {rail ? (
          <button
            type="button"
            onClick={toggleCollapsed}
            title="Expand sidebar"
            aria-label="Expand sidebar"
            className="group relative w-9 h-9 shrink-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 dark:focus-visible:ring-zinc-500"
          >
            <img
              src="/logo.png"
              alt=""
              className="w-9 h-9 rounded-lg object-cover transition-opacity group-hover:opacity-0"
            />
            <span className="absolute inset-0 flex items-center justify-center rounded-lg bg-zinc-900/90 dark:bg-zinc-100/90 opacity-0 group-hover:opacity-100 transition-opacity">
              <PanelLeft className="w-4 h-4 text-white dark:text-zinc-900" />
            </span>
          </button>
        ) : (
          <>
            <img
              src="/logo.png"
              alt="Blissful Intelligence"
              className="w-9 h-9 rounded-lg object-cover shrink-0"
            />
            <div className="ml-3 flex-1 min-w-0 overflow-hidden whitespace-nowrap">
              <h2 className="text-sm font-bold text-zinc-900 dark:text-white leading-tight">
                Blissful Intelligence
              </h2>
              <div className="text-[10px] text-zinc-500 font-medium">Coach Portal</div>
            </div>
            <button
              type="button"
              onClick={toggleCollapsed}
              title="Collapse sidebar"
              aria-label="Collapse sidebar"
              className="shrink-0 flex items-center justify-center w-8 h-8 rounded-full text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 dark:focus-visible:ring-zinc-500 ml-auto"
            >
              <PanelLeftClose className="w-4 h-4" />
            </button>
          </>
        )}
      </div>

      <nav
        className="flex flex-col gap-4 flex-1 px-4 pt-2 overflow-y-auto overflow-x-hidden no-scrollbar"
        aria-label="Primary"
      >
        {visibleNavGroups.map((group) => {
          const isCollapsed = collapsedGroups[group.id] ?? false;
          return (
            <div key={group.id} className="flex flex-col gap-1">
              {!rail && (
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  className="px-2 mb-1 flex items-center justify-between text-left group/nav-header"
                >
                  <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold group-hover/nav-header:text-zinc-700 dark:group-hover/nav-header:text-zinc-300 transition-colors">
                    {group.label}
                  </span>
                  <ChevronRight
                    className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${
                      isCollapsed ? 'rotate-90' : '-rotate-90'
                    }`}
                  />
                </button>
              )}
              <AnimatePresence initial={false}>
                {(!isCollapsed || rail) && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex flex-col gap-1 overflow-hidden"
                  >
                    {group.items.map(({ to, label, icon: Icon, end, children }) => {
                      const hasChildren = !rail && children && children.length > 0;
                      const expanded = hasChildren ? isItemExpanded(to) : false;
                      return (
                        <div key={to} className="flex flex-col">
                          <div className="relative flex items-center">
                            <NavLink
                              to={to}
                              end={end ?? false}
                              title={rail ? label : undefined}
                              className={({ isActive }) =>
                                `relative flex-1 flex items-center rounded-full text-xs font-medium transition-all duration-300 overflow-hidden ${
                                  rail
                                    ? 'justify-center px-0 py-2.5'
                                    : `justify-start gap-3 py-2.5 ${hasChildren ? 'pl-4 pr-9' : 'px-4'}`
                                } ${
                                  isActive
                                    ? 'text-white'
                                    : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 hover:bg-zinc-50 dark:hover:bg-zinc-800/50'
                                }`
                              }
                            >
                              {({ isActive }) => (
                                <>
                                  {isActive && (
                                    <motion.div
                                      layoutId="sidebarActiveIndicator"
                                      className="absolute inset-0 bg-brand-600 dark:bg-brand-500 rounded-full"
                                      transition={{
                                        type: 'spring' as const,
                                        stiffness: 400,
                                        damping: 30,
                                      }}
                                    />
                                  )}
                                  <div
                                    className={`relative z-10 flex items-center ${rail ? '' : 'gap-3'}`}
                                  >
                                    <Icon className="w-4 h-4" />
                                    {!rail && <span className="whitespace-nowrap">{label}</span>}
                                  </div>
                                </>
                              )}
                            </NavLink>
                            {hasChildren && (
                              <button
                                type="button"
                                onClick={() => toggleItem(to)}
                                aria-label={expanded ? `Collapse ${label}` : `Expand ${label}`}
                                aria-expanded={expanded}
                                className="absolute right-1.5 z-10 w-6 h-6 flex items-center justify-center rounded-full text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                              >
                                <ChevronRight
                                  className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`}
                                />
                              </button>
                            )}
                          </div>

                          {hasChildren && (
                            <AnimatePresence initial={false}>
                              {expanded && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.2 }}
                                  className="flex flex-col gap-0.5 overflow-hidden pl-9 pr-1 mt-0.5"
                                >
                                  {children.map((child) => {
                                    const active = isChildActive(
                                      location.pathname,
                                      location.search,
                                      child.to,
                                    );
                                    return (
                                      <Link
                                        key={child.to}
                                        to={child.to}
                                        className={`rounded-full text-xs font-medium px-3 py-2 transition-colors whitespace-nowrap ${
                                          active
                                            ? 'text-zinc-900 dark:text-zinc-100 bg-zinc-100 dark:bg-zinc-800/60 font-semibold'
                                            : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-50 dark:hover:bg-zinc-800/50'
                                        }`}
                                      >
                                        {child.label}
                                      </Link>
                                    );
                                  })}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          )}
                        </div>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </nav>
    </>
  );

  return (
    <div className="flex min-h-screen bg-page dark:bg-zinc-950 transition-colors duration-300">
      {/* Desktop Sidebar */}
      <motion.aside
        animate={{ width: collapsed ? SIDEBAR_RAIL_WIDTH : SIDEBAR_EXPANDED_WIDTH }}
        transition={{ type: 'spring' as const, stiffness: 300, damping: 30 }}
        className="hidden lg:flex flex-col h-screen sticky top-0 bg-white dark:bg-zinc-950 z-20 overflow-hidden"
      >
        <SidebarContent rail={collapsed} />
      </motion.aside>

      {/* Mobile Drawer Backdrop */}
      <AnimatePresence>
        {drawerOpen && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            type="button"
            aria-label="Close menu"
            className="fixed inset-0 z-40 bg-zinc-950/40 backdrop-blur-sm lg:hidden"
            onClick={() => setDrawerOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {drawerOpen && (
          <motion.aside
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring' as const, stiffness: 300, damping: 30 }}
            className="fixed inset-y-0 left-0 z-50 w-[260px] flex flex-col bg-white dark:bg-zinc-900 shadow-2xl lg:hidden"
          >
            <SidebarContent />
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-10 h-16 flex items-center justify-between gap-4 px-4 md:px-8 lg:px-10 bg-page/80 dark:bg-zinc-950/80 backdrop-blur-xl">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="flex items-center gap-4 lg:hidden shrink-0">
              <button
                type="button"
                className="flex items-center justify-center w-9 h-9 rounded-full bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-white/10 text-zinc-600 dark:text-zinc-300"
                onClick={() => setDrawerOpen(true)}
              >
                <MenuIcon width={18} height={18} />
              </button>
            </div>

            <div className="flex items-center gap-1.5 min-w-0 shrink-0">
              {breadcrumbForPath(location.pathname).map((segment, i, segments) => (
                <div key={i} className="flex items-center gap-1.5">
                  {segment.to ? (
                    <Link
                      to={segment.to}
                      className="text-sm font-medium text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 transition-colors"
                    >
                      {segment.label}
                    </Link>
                  ) : (
                    <span className="font-display font-semibold text-base text-zinc-900 dark:text-zinc-100 truncate">
                      {segment.label}
                    </span>
                  )}
                  {i < segments.length - 1 && (
                    <ChevronRight className="w-3.5 h-3.5 text-zinc-300 dark:text-zinc-600 shrink-0" />
                  )}
                </div>
              ))}
            </div>

            <div className="hidden md:flex items-center justify-between gap-2 bg-white dark:bg-zinc-900 px-3 h-9 rounded-full border border-zinc-200 dark:border-white/10 w-full max-w-[280px] ml-auto">
              <div className="flex items-center gap-2 flex-1">
                <Search className="w-3.5 h-3.5 text-zinc-400" />
                <input
                  type="text"
                  placeholder="Search here..."
                  className="bg-transparent border-none outline-none text-xs w-full text-zinc-900 dark:text-zinc-100 placeholder-zinc-400"
                />
              </div>
              <div className="flex items-center gap-2">
                <kbd className="hidden lg:inline-flex items-center justify-center px-1.5 py-0.5 text-[9px] font-semibold text-zinc-500 font-mono">
                  ⌘S
                </kbd>
                <button className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors">
                  <SlidersHorizontal className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2.5 lg:gap-3 shrink-0">
            <div className="hidden sm:flex items-center bg-white dark:bg-zinc-900 rounded-full p-1 border border-zinc-200 dark:border-white/10 h-9">
              <button
                onClick={toggleTheme}
                className={`rounded-full transition-all flex items-center justify-center w-7 h-7 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 dark:focus-visible:ring-zinc-500 ${theme === 'light' ? 'bg-brand-600 text-white shadow-md' : 'text-zinc-400 hover:text-zinc-900 dark:hover:text-white'}`}
              >
                <Sun className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={toggleTheme}
                className={`rounded-full transition-all flex items-center justify-center w-7 h-7 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 dark:focus-visible:ring-zinc-500 ${theme === 'dark' ? 'bg-zinc-800 text-white shadow-md' : 'text-zinc-400 hover:text-zinc-900 dark:hover:text-white'}`}
              >
                <Moon className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="relative" ref={notificationsRef}>
              <button
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                className="relative w-9 h-9 flex items-center justify-center bg-white dark:bg-zinc-900 rounded-full border border-zinc-200 dark:border-white/10 text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white transition-colors"
              >
                <Bell className="w-4 h-4" />
                {(notifications.data?.unreadCount ?? 0) > 0 && (
                  <span className="absolute top-2 right-2 w-1.5 h-1.5 bg-orange-500 rounded-full border border-white dark:border-zinc-900 box-content"></span>
                )}
              </button>

              <AnimatePresence>
                {notificationsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-zinc-900 rounded-2xl shadow-xl border border-zinc-200 dark:border-white/10 z-50 overflow-hidden"
                  >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100 dark:border-white/5">
                      <p className="text-sm font-semibold text-zinc-900 dark:text-white">
                        Notifications
                      </p>
                      {(notifications.data?.unreadCount ?? 0) > 0 && (
                        <button
                          onClick={() => markAllNotificationsRead.mutate()}
                          className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:underline"
                        >
                          Mark all as read
                        </button>
                      )}
                    </div>

                    <div className="max-h-96 overflow-y-auto">
                      {(notifications.data?.notifications.length ?? 0) === 0 ? (
                        <p className="px-4 py-6 text-sm text-zinc-500 text-center">
                          No notifications yet
                        </p>
                      ) : (
                        notifications.data?.notifications.map((n) => (
                          <button
                            key={n.id}
                            onClick={() => {
                              if (!n.isRead) markNotificationRead.mutate(n.id);
                              setNotificationsOpen(false);
                              if (n.link) navigate(n.link);
                            }}
                            className={`w-full flex flex-col gap-0.5 text-left px-4 py-3 border-b border-zinc-50 dark:border-white/5 last:border-0 transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800 ${
                              n.isRead ? '' : 'bg-brand-50/50 dark:bg-brand-500/10'
                            }`}
                          >
                            <span
                              className={`text-sm ${n.isRead ? 'text-zinc-600 dark:text-zinc-300' : 'font-semibold text-zinc-900 dark:text-zinc-100'}`}
                            >
                              {n.title}
                            </span>
                            <span className="text-xs text-zinc-500 dark:text-zinc-400">
                              {n.body}
                            </span>
                            <span className="text-[11px] text-zinc-400 mt-0.5">
                              {new Date(n.createdAt).toLocaleString(undefined, {
                                day: 'numeric',
                                month: 'short',
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {coach && (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 pl-1 pr-3 h-9 bg-white dark:bg-zinc-900 rounded-full border border-zinc-200 dark:border-white/10 transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800"
                >
                  <img
                    src={`https://api.dicebear.com/7.x/notionists/svg?seed=${coach.name}`}
                    alt="Avatar"
                    className="w-7 h-7 rounded-full bg-zinc-200 dark:bg-zinc-800 border-2 border-white dark:border-zinc-900"
                  />
                  <span className="text-xs font-medium text-zinc-900 dark:text-zinc-100 hidden sm:block leading-none">
                    {coach.name}
                  </span>
                </button>

                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-zinc-900 rounded-2xl shadow-xl border border-zinc-200 dark:border-white/10 z-50 overflow-hidden py-2"
                    >
                      <div className="px-4 py-3 border-b border-zinc-100 dark:border-white/5 mb-2">
                        <p className="text-sm font-semibold text-zinc-900 dark:text-white">
                          {coach.name}
                        </p>
                        <p className="text-xs text-zinc-500 mt-0.5">{coach.email}</p>
                      </div>
                      <Link
                        to="/profile"
                        onClick={() => setUserMenuOpen(false)}
                        className="w-full flex items-center gap-3 text-left px-4 py-2.5 text-sm text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                      >
                        <User className="w-4 h-4" />
                        Profile
                      </Link>
                      <Link
                        to="/settings"
                        onClick={() => setUserMenuOpen(false)}
                        className="w-full flex items-center gap-3 text-left px-4 py-2.5 text-sm text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                      >
                        <Settings className="w-4 h-4" />
                        Settings
                      </Link>
                      <button
                        onClick={() => void logout()}
                        className="w-full flex items-center gap-3 text-left px-4 py-2.5 text-sm text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign out
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 w-full max-w-[1400px] mx-auto p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
